import React, { useEffect, useState } from "react";
import { Smartphone, Apple, Monitor, Download, X, Loader } from "lucide-react";
import { Button } from "./ui/button";

// sessionStorage key — once a user dismisses the prompt we don't show
// it again until they close the tab. Per-tab, not per-account, because
// the prompt is purely informational.
const DISMISS_KEY = "aithlo.mobilePrompt.dismissed";

// Public: a phone can install the app before it has an account.
export const ANDROID_APK_URL = `${import.meta.env.VITE_API_URL}/mobile/download/android`;

/**
 * Heuristic mobile detection. Combines a viewport check (≤768 wide)
 * with a coarse user-agent sniff so we don't promote the Android APK
 * to laptop browsers, even when the window happens to be narrow.
 */
export function isMobileDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const uaIsMobile = /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua);
  const narrowViewport = window.innerWidth <= 768;
  return uaIsMobile && narrowViewport;
}

/** Returns true when the device is specifically Android. */
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

/**
 * Mobile-only modal shown on phones, signed in or not: the APK needs no
 * account. Recommends the Android APK download, notes iOS is on the way,
 * and explains that the web app is built for laptop/desktop. Dismissible —
 * the user can "ignore" and continue with the web app on their phone.
 *
 * Renders BEFORE the onboarding modal in Layout.jsx so first-time
 * mobile users see the device guidance before the setup wizard pops.
 */
const MobileAppPromptModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Only show once per browser tab session, and only on mobile.
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      // sessionStorage unavailable (private mode quirks) — fall through;
      // we'll show the prompt and rely on the in-memory state of this
      // component to suppress it after dismissal.
    }
    if (!dismissed && isMobileDevice()) {
      setIsOpen(true);
    }
  }, []);

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
    setIsOpen(false);
  };

  const handleDownloadApk = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    // /mobile/download/android now 302-redirects to a public Release
    // asset. Use a plain anchor click so the browser handles the
    // redirect chain natively — see Settings.jsx::triggerDownload for
    // the full reasoning. We can't observe completion from here, so
    // we flash the loading state briefly and dismiss the modal.
    const a = document.createElement("a");
    a.href = ANDROID_APK_URL;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Mark dismissed so the prompt doesn't pop up again — they've made
    // their choice. Defer slightly so the browser's download UI has
    // time to attach before the modal vanishes.
    setTimeout(() => {
      setIsDownloading(false);
      dismiss();
    }, 1500);
  };

  if (!isOpen) return null;

  const showAndroidPrimary = isAndroid();

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        backgroundColor: "rgba(15, 23, 42, 0.6)",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-prompt-title"
    >
      <div
        className="w-full max-w-md bg-bg-surface rounded-2xl overflow-hidden shadow-2xl"
        style={{ boxShadow: "0 24px 48px -12px rgba(20, 184, 166, 0.35), 0 0 0 1px rgba(20, 184, 166, 0.08)" }}
      >
        {/* Hero band */}
        <div
          className="relative px-6 py-7 text-center text-white overflow-hidden"
          style={{
            background: "linear-gradient(120deg, #0d9488 0%, #06b6d4 50%, #6366f1 100%)",
            backgroundSize: "200% 200%",
            animation: "aiGradientShift 8s ease-in-out infinite",
          }}
        >
          <div className="absolute -top-10 -right-8 w-36 h-36 rounded-full bg-white/15 blur-3xl" />
          <div className="absolute -bottom-12 -left-10 w-32 h-32 rounded-full bg-indigo-400/30 blur-3xl" />

          <div className="relative">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm items-center justify-center ring-2 ring-white/40 mb-3">
              <Smartphone size={26} />
            </div>
            <h2 id="mobile-prompt-title" className="text-xl font-bold tracking-tight">
              Better on the big screen
            </h2>
            <p className="text-sm text-white/90 mt-1 max-w-xs mx-auto leading-relaxed">
              The Aithlo web app is built for laptop &amp; desktop — for the best
              experience on phone, install the mobile app instead.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          {/* Android — primary CTA */}
          <div className="rounded-xl border border-accent-tint-border bg-accent-tint p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent-tint text-accent-default flex items-center justify-center flex-shrink-0">
                <Smartphone size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary">Android app</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Available now as a direct download (.apk).
                </p>
              </div>
            </div>
            <Button
              onClick={handleDownloadApk}
              disabled={isDownloading}
              className="w-full mt-3 gap-2"
              size="sm"
            >
              {isDownloading ? (
                <>
                  <Loader size={14} className="animate-spin" /> Downloading…
                </>
              ) : (
                <>
                  <Download size={14} /> Download Android APK
                </>
              )}
            </Button>
            {!showAndroidPrimary && (
              <p className="text-[11px] text-text-tertiary mt-2 text-center">
                You'll need to allow installation from this browser when prompted.
              </p>
            )}
          </div>

          {/* iOS — coming soon */}
          <div className="rounded-xl border border-border-default p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-bg-raised text-text-secondary flex items-center justify-center flex-shrink-0">
                <Apple size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary">iOS app</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Coming soon — we'll let you know when it's ready.
                </p>
              </div>
            </div>
          </div>

          {/* Desktop nudge */}
          <div className="rounded-xl bg-bg-raised border border-border-subtle p-4 flex items-start gap-3">
            <Monitor size={16} className="text-text-tertiary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-text-secondary leading-relaxed">
              Prefer to use the web app? It's fully featured on your laptop or
              desktop browser at <span className="font-medium text-accent-default">aithlo.com</span>.
            </p>
          </div>

          {/* Ignore — keep using mobile web */}
          <button
            type="button"
            onClick={dismiss}
            className="w-full text-sm text-text-tertiary hover:text-text-body py-2 underline-offset-2 hover:underline"
          >
            Ignore — continue in the mobile browser
          </button>
        </div>

        {/* Top-right close (same as ignore) */}
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default MobileAppPromptModal;
